import {
  type CoverageModel,
  PLACEHOLDER_EMITTERS,
  type SensorStation,
  type SurveillanceEnvelope,
  type SurveillanceService,
  isServiceEmitter,
  orderServices,
  serviceLabel,
} from '@/lib/mqtt/schemas/surveillance';
import { circle as turfCircle } from '@turf/turf';
/**
 * v1 `surveillance_sensors/<emitter>` adapter.
 *
 * Each emitter publishes a snapshot of its receiver pool at low cadence
 * (~1 msg / 60 s). The envelope shape is consistent across emitters:
 *
 *   { timestamp, online_receivers, expected_receivers,
 *     latest_update_seconds, url, devices: [...] }
 *
 * Regional emitters (`live_regional_<region>`) ALSO include
 * envelope-level `lat`, `lon`, `circle_radius_miles` describing the
 * regional aggregator's coverage centre + radius — used to synthesise a
 * v2 `coverageModels[]` polygon. Other emitters don't ship coverage.
 *
 * Device-record shape varies by emitter:
 *
 *   live_vision        — rich: serial_number, identifier_hex, lat/lon,
 *                        altitude_mm, gps_fix, connected, rtt_ms,
 *                        uptime, last_update (epoch ms), version, type
 *   live_mlat          — lean: id, lat/lon, model ("G-1090 UAT")
 *   live_regional_*    — id, lat/lon, peers, distance_km, source
 *   live_community     — id, lat/lon, peers, per-region distance fields
 *                        (distance_miles_edm / _cal / _fmm)
 *
 * The v2 `SurveillanceEnvelope` lists FIVE canonical services. v1 has
 * more emitters (regional aggregators). We consolidate:
 *
 * Ticket 86e1fvf06 — un-merged the regionals so each is its own pill /
 * service with its own coverage circle. Excluded emitters are filtered
 * out via the ALLOWED_EMITTERS_SET allowlist in schemas/surveillance.ts.
 *
 *   live_ads                       → live_ads          (cooperative ADS-B, no coverage circle)
 *   live_vision                    → live_vision
 *   live_mlat                      → live_mlat
 *   live_drone_rf                  → live_drone_rf     (Non-Cooperative; silent on staging today)
 *   live_regional_edmonton         → live_regional_edmonton       (each regional now standalone,
 *   live_regional_calgary          → live_regional_calgary         with its own coverage circle
 *   live_regional_fort_mcmurray    → live_regional_fort_mcmurray    from envelope lat/lon/radius)
 *
 *   live_community / live_ads_count / metrics / stream  → dropped (DO NOT ADD per ticket)
 *
 * The map layer renders stations as pins; STATION_CAP keeps the DOM-marker
 * render smooth (v1 community emitter alone could publish 984 receivers).
 */
import { z } from 'zod';

/** Max stations rendered per service. v1 community emitter publishes
 *  984 receivers globally — drawing all of them as DOM markers kills
 *  the render loop. Cap closest-by-distance (or earliest seen). */
const STATION_CAP = 30;

/** A surveillance service id — the topic suffix, e.g. `live_ads` or whatever
 *  else the broker reports. Not limited to the curated `ServiceId` names. */
export type V2ServiceId = string;

/** Map a v1 emitter (topic suffix) to its service id, or null when the emitter
 *  is not a surveillance service (the ticket's "DO NOT ADD" list: live_community
 *  / live_ads_count / metrics / stream, or anything not shaped `live_…`).
 *  Everything else the broker reports is accepted — the list follows the broker.
 *  The legacy `live_adsb` alias still maps to `live_ads`. */
export function emitterToServiceId(emitter: string): V2ServiceId | null {
  if (emitter === 'live_adsb') return 'live_ads'; // legacy alias
  return isServiceEmitter(emitter) ? emitter : null;
}

/** Trust mapping per service (mirrors v1 source-priority intent):
 *  cooperative ADS-B + MLAT + Vision = high; regional aggregators and
 *  drone-RF = medium. live_community is excluded entirely and never
 *  reaches this function. */
export function trustForService(id: V2ServiceId): SurveillanceService['trust'] {
  switch (id) {
    case 'live_ads':
    case 'live_vision':
    case 'live_mlat':
    // Synthesised client-side by the Live_Fused panel, never returned by
    // `emitterToServiceId`.
    case 'live_fused':
      return 'High';
    // drone-RF and the regional aggregators — and any service nobody has
    // classified yet: not the cleanest feed, not a bad one.
    default:
      return 'Medium';
  }
}

/** Cooperative/non-cooperative mapping. Drone_RF detects uncooperative
 *  drones (RF-based, not transponder-based) so it's the only non-coop
 *  source in the allowlist. */
export function typeForService(id: V2ServiceId): SurveillanceService['type'] {
  return id === 'live_drone_rf' ? 'Non-Cooperative' : 'Cooperative';
}

/** Derive service state from the station list. Replaces the old
 *  counts-based heuristic with the station-status rules requested in
 *  the surveillance Connecting/Degraded follow-up:
 *
 *    • any station `degraded` or `offline` → service `Degraded`
 *    • all stations `live`                 → service `Active`
 *    • zero stations + envelope received   → fall back to counts
 *                                            (`online_sensors > 0` → Active,
 *                                            otherwise Offline)
 *
 *  This matches mockup `Live_MLAT` (4 live + 1 degraded + 1 offline →
 *  `Degraded`) — both non-live conditions trigger the Degraded badge so
 *  operators see at-a-glance whether any receiver is misbehaving. */
export function stateFromStations(
  stations: SensorStation[],
  online: number,
): SurveillanceService['service_state'] {
  if (stations.length === 0) {
    return online > 0 ? 'Active' : 'Offline';
  }
  for (const s of stations) {
    if (s.state !== 'live') return 'Degraded';
  }
  return 'Active';
}

/* ------------------ envelope + device Zod schemas ------------------ */

export const V1SurveillanceSensorsPayload = z
  .object({
    timestamp: z.string().nullish(),
    online_receivers: z.number().int().nonnegative().nullish(),
    expected_receivers: z.number().int().nonnegative().nullish(),
    latest_update_seconds: z.number().nullish(),
    url: z.string().nullish(),
    devices: z.array(z.unknown()).nullish(),
    // Regional emitters only:
    lat: z.number().nullish(),
    lon: z.number().nullish(),
    circle_radius_miles: z.number().nullish(),
  })
  .passthrough();
export type V1SurveillanceSensorsPayload = z.infer<typeof V1SurveillanceSensorsPayload>;

/** Loose device schema — covers all four shapes; missing fields are fine. */
const V1Device = z
  .object({
    id: z.union([z.string(), z.number()]).nullish(),
    serial_number: z.string().nullish(),
    identifier_hex: z.string().nullish(),
    lat: z.number().nullish(),
    lon: z.number().nullish(),
    peers: z.number().nullish(),
    model: z.string().nullish(),
    connected: z.boolean().nullish(),
    rtt_ms: z.number().nullish(),
    last_update: z.number().nullish(), // epoch ms (vision)
    distance_km: z.number().nullish(), // regional
    distance_miles: z.number().nullish(), // regional
  })
  .passthrough();
type V1Device = z.infer<typeof V1Device>;

/* ------------------ helpers ------------------ */

/** Normalise the v1 +00:00 / Z timestamp into UTC "Z" form so Zod's
 *  `.datetime({ offset: true })` accepts it. */
function normaliseIsoUtc(s: string | null | undefined): string {
  if (!s) return new Date().toISOString();
  const t = Date.parse(s);
  if (Number.isNaN(t)) return new Date().toISOString();
  return new Date(t).toISOString();
}

/** Sort devices so the most relevant ones survive the STATION_CAP cut.
 *  Distance (km) ascending when available; otherwise by `peers` count
 *  descending (more peers = more central node). Stable for ties. */
function rankDevices(devices: V1Device[]): V1Device[] {
  return [...devices].sort((a, b) => {
    if (a.distance_km != null && b.distance_km != null) return a.distance_km - b.distance_km;
    if (a.distance_km != null) return -1;
    if (b.distance_km != null) return 1;
    const pa = a.peers ?? -1;
    const pb = b.peers ?? -1;
    return pb - pa;
  });
}

// Doc 007 — Sensor State Logic:
//   heartbeat < 30s  AND  rtt_ms < 200ms        → live      (green)
//   heartbeat < 30s  AND  rtt_ms 200-400ms      → degraded  (amber)
//   no heartbeat > 30s  OR  last_seen > 4 min   → offline   (gray)
// The two thresholds operate on different signals:
//   - `heartbeat` = age of the last per-device update (vision's
//     `last_update`); for non-vision emitters this falls back to the
//     envelope timestamp because per-device last-seen isn't published.
//   - `rtt_ms` = round-trip time (only vision publishes this).
//
// **v1 deviation from doc 007:** the spec's 30 s / 4 min thresholds assume
// every emitter heartbeats inside 30 s. v1's `surveillance_sensors/live_regional_*`
// (folded into live_ADSB), `live_community` and `live_drone_rf` republish
// every 30–90 s with occasional multi-minute gaps. Holding to the canonical
// thresholds made the services flap (live → degraded → live → offline) every
// broker cycle even when nothing was wrong. We widen to v1's observed cadence:
//   degraded  — heartbeat > 120 s   (4× v1's slowest normal cycle)
//   offline   — heartbeat > 10 min  (true silence, not just a slow cycle)
// Revisit when the canonical envelope ships with a per-emitter heartbeat
// guarantee — at that point we go back to 30 s / 4 min.
const HEARTBEAT_DEGRADED_MS = 120_000; // 2 min (was 30 s; v1 broker cadence)
const HEARTBEAT_OFFLINE_MS = 10 * 60_000; // 10 min (was 4 min; v1 broker cadence)
const RTT_DEGRADED_MS = 200;
const RTT_OFFLINE_MS = 400;

function deriveSensorState(d: V1Device, lastSeenMs: number, nowMs: number): SensorStation['state'] {
  if (d.connected === false) return 'offline';
  const heartbeatAgeMs = Math.max(0, nowMs - lastSeenMs);
  // Offline takes precedence: a hard-stale sensor stays offline even
  // if its last-known rtt was OK.
  if (heartbeatAgeMs > HEARTBEAT_OFFLINE_MS) return 'offline';
  if (d.rtt_ms != null && d.rtt_ms >= RTT_OFFLINE_MS) return 'offline';
  if (heartbeatAgeMs > HEARTBEAT_DEGRADED_MS) return 'degraded';
  if (d.rtt_ms != null && d.rtt_ms >= RTT_DEGRADED_MS) return 'degraded';
  return 'live';
}

/** Convert a v1 device record into a v2 SensorStation. Returns null if
 *  the device lacks coords (can't render without lat/lng). */
function deviceToStation(d: V1Device, envelopeTimestampIso: string): SensorStation | null {
  if (d.lat == null || d.lon == null) return null;
  // sensor_id: prefer serial_number (vision), then string id, then a
  // fallback from coords. Stringify number IDs so the v2 type matches.
  const sensorId =
    d.serial_number?.trim() ||
    (d.id != null ? String(d.id) : `pos_${d.lat.toFixed(4)}_${d.lon.toFixed(4)}`);
  // label: vision uses serial; mlat uses id+model; community uses id; etc.
  const label =
    (d.model?.trim() ? `${d.id ?? sensorId} · ${d.model.trim()}` : null) ??
    d.serial_number?.trim() ??
    String(d.id ?? sensorId);
  // last_seen: vision device has epoch-ms `last_update`; otherwise fall
  // back to the envelope's timestamp.
  const envelopeMs = Date.parse(envelopeTimestampIso);
  const lastSeenMs = d.last_update != null ? d.last_update : envelopeMs;
  const lastSeen = new Date(lastSeenMs).toISOString();
  const state = deriveSensorState(d, lastSeenMs, Date.now());
  return {
    sensor_id: sensorId,
    label,
    lat: d.lat,
    lng: d.lon,
    state,
    rtt_ms: d.rtt_ms ?? null,
    latency_ms: null, // not in v1
    rssi: null, // not in v1
    target_count: d.peers ?? 0, // peers ≈ visible targets
    last_seen: lastSeen,
  };
}

/** Synthesise a v2 `CoverageModel` from a regional emitter's centre +
 *  radius. Uses turf to render a 64-step circle ring. We tag AGL=0
 *  (the radius is ground-level coverage; v2 supports tiered AGL slices
 *  but v1 doesn't publish them). */
function coverageFromCircle(
  lat: number,
  lon: number,
  radiusMiles: number,
  createdAtIso: string,
  sensorIds: string[],
): CoverageModel {
  const km = radiusMiles * 1.60934;
  const c = turfCircle([lon, lat], km, { steps: 64, units: 'kilometers' });
  // turf returns a Feature<Polygon> with [[lng,lat], ...] in
  // coordinates[0]. Strip the GeoJSON wrap to fit the v2 schema's
  // bare [lng,lat] ring array.
  const ring = (c.geometry.coordinates[0] ?? []) as [number, number][];
  return {
    agl: 0,
    sensors: sensorIds,
    createdAt: createdAtIso,
    polygon: ring,
  };
}

/* ------------------ per-emitter snapshot model ------------------ */

/** What we keep per v1 emitter after parsing. The source folds these
 *  into the 5 canonical services on each unified emit. */
export interface EmitterSnapshot {
  emitter: string;
  service_id: V2ServiceId;
  receivedAtIso: string;
  online: number;
  expected: number;
  /** Capped + ranked stations from devices[]. */
  stations: SensorStation[];
  /** Optional coverage circle (only regional emitters supply this). */
  coverage: CoverageModel | null;
}

/** Parse a single v1 sensors message into a per-emitter snapshot, or
 *  null if the payload is malformed or the emitter is unmapped (stream
 *  aggregate / unknown emitter). */
export function parseSurveillanceSensors(emitter: string, raw: unknown): EmitterSnapshot | null {
  const serviceId = emitterToServiceId(emitter);
  if (!serviceId) return null;
  const parsed = V1SurveillanceSensorsPayload.safeParse(raw);
  if (!parsed.success) return null;
  const env = parsed.data;
  const ts = normaliseIsoUtc(env.timestamp);

  // Convert devices → stations, then cap.
  const devicesRaw: V1Device[] = [];
  for (const d of env.devices ?? []) {
    const p = V1Device.safeParse(d);
    if (p.success) devicesRaw.push(p.data);
  }
  const ranked = rankDevices(devicesRaw);
  const stations: SensorStation[] = [];
  for (const d of ranked) {
    if (stations.length >= STATION_CAP) break;
    const s = deviceToStation(d, ts);
    if (s) stations.push(s);
  }

  // Coverage only if regional fields are present.
  let coverage: CoverageModel | null = null;
  if (
    env.lat != null &&
    env.lon != null &&
    env.circle_radius_miles &&
    env.circle_radius_miles > 0
  ) {
    coverage = coverageFromCircle(
      env.lat,
      env.lon,
      env.circle_radius_miles,
      ts,
      stations.map((s) => s.sensor_id),
    );
  }

  return {
    emitter,
    service_id: serviceId,
    receivedAtIso: ts,
    online: env.online_receivers ?? 0,
    expected: env.expected_receivers ?? 0,
    stations,
    coverage,
  };
}

/** Re-derive sensor state at fold time. Lets a station transition from
 *  `live` → `degraded` → `offline` as its `last_seen` ages, even when no
 *  new message has arrived for that emitter. `offline` is sticky once
 *  set — a brief disconnect that recovers requires the next published
 *  message to clear it (deviceToStation re-evaluates from the fresh
 *  V1Device). */
function refreshSensorState(s: SensorStation, nowMs: number): SensorStation['state'] {
  if (s.state === 'offline') return 'offline';
  const lastSeenMs = Date.parse(s.last_seen);
  const ageMs = nowMs - lastSeenMs;
  if (!Number.isFinite(lastSeenMs)) return s.state;
  if (ageMs > HEARTBEAT_OFFLINE_MS) return 'offline';
  if (s.rtt_ms != null && s.rtt_ms >= RTT_OFFLINE_MS) return 'offline';
  if (ageMs > HEARTBEAT_DEGRADED_MS) return 'degraded';
  if (s.rtt_ms != null && s.rtt_ms >= RTT_DEGRADED_MS) return 'degraded';
  return 'live';
}

/** Fold per-emitter snapshots into the surveillance services. The list follows
 *  the broker: a service is listed once it has been seen on a sensors topic
 *  (`snapshots`), and stays listed — as Offline — after it goes quiet
 *  (`discovered` remembers every id seen this session, because snapshots are
 *  evicted after SURVEILLANCE_STALE_MS). While the cold-start window is open
 *  the curated services the broker normally reports are shown as "Connecting…"
 *  so the list is not empty for up to a minute and a half; past it, one that
 *  never reported is dropped rather than shown as a permanent Offline row.
 *  Each emitter is its own service, so a group is normally one snapshot — the
 *  SUM/CONCAT loops still work if the broker ever publishes several under one
 *  id. Capped at STATION_CAP per service overall.
 *
 *  `connectingUntilMs` is the wall-clock end of the cold-start window. The
 *  caller (MqttSourceLegacy) computes it from MQTT connect time +
 *  SURVEILLANCE_CONNECTING_MS so the buffer survives across calls. */
export function foldSnapshotsToServices(
  snapshots: Iterable<EmitterSnapshot>,
  connectingUntilMs = 0,
  discovered: Iterable<string> = [],
  trafficLive: ReadonlySet<string> = new Set(),
  visibleByService: ReadonlyMap<string, readonly string[]> = new Map(),
): SurveillanceService[] {
  const grouped = new Map<V2ServiceId, EmitterSnapshot[]>();
  for (const s of snapshots) {
    let arr = grouped.get(s.service_id);
    if (!arr) {
      arr = [];
      grouped.set(s.service_id, arr);
    }
    arr.push(s);
  }

  const out: SurveillanceService[] = [];
  const nowMs = Date.now();
  const ids = orderServices([
    ...grouped.keys(),
    ...discovered,
    ...(nowMs < connectingUntilMs ? PLACEHOLDER_EMITTERS : []),
  ]);
  for (const sid of ids) {
    const group = grouped.get(sid) ?? [];
    const hasReceivedData = group.length > 0;
    const publisherOnline = group.reduce((a, s) => a + s.online, 0);
    const expected = group.reduce((a, s) => a + s.expected, 0);
    const stations: SensorStation[] = [];
    const coverageModels: CoverageModel[] = [];
    let demoted = 0;
    for (const s of group) {
      for (const st of s.stations) {
        if (stations.length >= STATION_CAP) break;
        // Re-derive state every fold so freshness reflects wall-clock,
        // not just the moment we parsed the broker message. v1 publishes
        // per emitter at ~30-90 s — by the time the next snapshot lands
        // the previous one's "live" stations may have aged into stale
        // territory.
        const refreshed = refreshSensorState(st, nowMs);
        if (st.state === 'live' && refreshed !== 'live') demoted++;
        stations.push({ ...st, state: refreshed });
      }
      if (s.coverage) coverageModels.push(s.coverage);
    }
    const online = Math.max(0, publisherOnline - demoted);
    // Coverage geometry is canonical-backend-owned (Flink Coverage
    // Validator Job per doc 007 — open gap #12). We render only what
    // v1's broker actually publishes: regional ADSB circles. Services
    // without coverage data ship `coverageModels: []` and render no
    // polygon in diagnostic mode — that's the honest signal that the
    // backend hasn't filled the gap yet.

    // State derivation:
    //   has data → station-based rules (Active / Degraded / Offline)
    //   no data + within buffer → Connecting (honest "waiting" signal
    //                              instead of misleading "Offline")
    //   no data + buffer expired → Offline (broker really is silent
    //                              for this service)
    //   no sensors topic but traffic flowing → Active (a service can publish
    //                              targets without ever publishing sensors)
    const service_state: SurveillanceService['service_state'] = hasReceivedData
      ? stateFromStations(stations, online)
      : trafficLive.has(sid)
        ? 'Active'
        : nowMs < connectingUntilMs
          ? 'Connecting'
          : 'Offline';

    out.push({
      service_id: sid,
      label: serviceLabel(sid),
      type: typeForService(sid),
      service_state,
      trust: trustForService(sid),
      online_sensors: online,
      total_sensors: expected,
      avg_latency_ms: null, // not in v1
      stations,
      visibleTargets: [...(visibleByService.get(sid) ?? [])], // from the traffic feed, not the sensors
      coverageModels,
    });
  }
  return out;
}

/** Build a complete `SurveillanceEnvelope` from the current set of
 *  per-emitter snapshots. Call this on the source's emit interval.
 *  `connectingUntilMs` is forwarded to `foldSnapshotsToServices` to
 *  drive the Connecting/Offline cold-start logic. */
export function snapshotsToEnvelope(
  snapshots: Iterable<EmitterSnapshot>,
  connectingUntilMs = 0,
  discovered: Iterable<string> = [],
  trafficLive: ReadonlySet<string> = new Set(),
  visibleByService: ReadonlyMap<string, readonly string[]> = new Map(),
): SurveillanceEnvelope {
  return {
    services: foldSnapshotsToServices(
      snapshots,
      connectingUntilMs,
      discovered,
      trafficLive,
      visibleByService,
    ),
    received_at: new Date().toISOString(),
  };
}
